import type { MinCopy } from '@/i18n/types'

export const minRu: MinCopy = {
  brand: {
    name: 'Lexio',
    tagline: 'Понимай английский, пока читаешь',
    beta: 'Beta',
  },
  nav: {
    today: 'Сегодня',
    catalog: 'Каталог',
    words: 'Словарь',
    reading: 'Чтение',
    refresh: 'Повторение',
    challenge: 'Проверка',
    progress: 'Прогресс',
    practice: 'Практика',
    settings: 'Настройки',
    feedback: 'Обратная связь',
    faq: 'FAQ',
    mobileWords: 'Слова',
    mobileReview: 'Повтор',
    more: 'Ещё',
    logout: 'Выйти',
    mobileNavAria: 'Мобильная навигация',
    closeMenuAria: 'Закрыть меню',
    close: 'Закрыть',
  },
  auth: {
    localeSwitcherLabel: 'Язык интерфейса',
    loginTitle: 'Lexio',
    loginDescription: 'Войди, чтобы продолжить изучение языка.',
    email: 'Email',
    password: 'Пароль',
    forgotPassword: 'Забыли пароль?',
    signIn: 'Войти',
    or: 'или',
    continueWithGoogle: 'Продолжить с Google',
    noAccount: 'Нет аккаунта?',
    registerLink: 'Регистрация',
    invalidEmail: 'Некорректный email',
    passwordRequired: 'Введите пароль',
    googleAuthFailed: 'Не удалось войти через Google. Попробуй email и пароль или зайди позже.',
    unableSignIn: 'Сейчас не получается войти. Попробуй ещё раз.',
    invalidCredentials: 'Неверный email или пароль',
    tooManyAttempts: 'Слишком много попыток. Попробуй позже.',
    registerTitle: 'Создай аккаунт Lexio',
    registerDescription: 'Словарь, чтение и повторение — в одном месте.',
    displayName: 'Имя',
    confirmPassword: 'Подтверди пароль',
    createAccount: 'Создать аккаунт',
    agreePrefix: 'Создавая аккаунт, ты принимаешь',
    termsOfService: 'Условия использования',
    agreeAnd: 'и',
    privacyPolicy: 'Политику конфиденциальности',
    agreeSuffix: '.',
    haveAccount: 'Уже есть аккаунт?',
    signInLink: 'Войти',
    passwordMin: 'Пароль — не меньше 8 символов',
    passwordUpper: 'Нужна хотя бы 1 заглавная буква',
    passwordDigit: 'Нужна хотя бы 1 цифра',
    passwordsMismatch: 'Пароли не совпадают',
    unableRegister: 'Сейчас не получается создать аккаунт. Попробуй ещё раз.',
    emailAlreadyRegistered: 'Этот email уже зарегистрирован',
    resetTitle: 'Сброс пароля',
    resetDescription: 'Укажи email — мы отправим ссылку для сброса.',
    sendResetLink: 'Отправить ссылку',
    backTo: 'Назад к',
    resetSent:
      'Если аккаунт с таким email есть, ссылка скоро придёт. Проверь также папку «Спам».',
    resetTooMany: 'Слишком много запросов с этого устройства. Подожди несколько минут.',
    resetUnable: 'Сейчас не получается отправить ссылку. Попробуй позже.',
    chooseNewPasswordTitle: 'Новый пароль',
    chooseNewPasswordDescription: 'Придумай надёжный пароль, чтобы снова войти в аккаунт.',
    newPassword: 'Новый пароль',
    confirmNewPassword: 'Подтверди новый пароль',
    saveNewPassword: 'Сохранить пароль',
    rememberedPassword: 'Вспомнил пароль?',
    resetLinkInvalid: 'Ссылка для сброса недействительна или устарела.',
    resetUnableConfirm: 'Сейчас не получается сбросить пароль. Попробуй позже.',
    passwordResetSuccess: 'Пароль обновлён. Войди в аккаунт.',
    checkEmailTitle: 'Проверь почту',
    verifyingTitle: 'Подтверждаем email',
    verifyingMessage: 'Подтверждаем email…',
    verificationSent:
      'Мы отправили ссылку для подтверждения на твой email. Проверь также папку «Спам».',
    emailPlaceholder: 'you@example.com',
    sendVerificationLink: 'Отправить ссылку ещё раз',
    alreadyVerified: 'Уже подтвердил?',
    verifyingHint: 'Не закрывай страницу, пока мы активируем аккаунт.',
    verificationLinkInvalid: 'Ссылка недействительна или устарела. Запроси новую ниже.',
    enterRegisteredEmail: 'Введи email, который использовал при регистрации.',
    verificationResent:
      'Если аккаунт ещё не подтверждён, новая ссылка отправлена. Проверь также «Спам».',
    verificationTooMany: 'Слишком много запросов. Попробуй позже.',
    verificationUnable: 'Сейчас не получается отправить ссылку. Попробуй ещё раз.',
    signingInWithGoogle: 'Входим через Google…',
  },
  onboarding: {
    wizardTitle: 'Добро пожаловать в Lexio',
    wizardSubtitle: 'Расскажи немного о себе — так мы подстроим обучение.',
    stepUiTitle: 'На каком языке показывать приложение?',
    stepUiDescription: 'Меню, кнопки и intro будут на этом языке. Позже можно изменить в Настройках.',
    uiLanguageLabel: 'Язык интерфейса',
    stepNativeTitle: 'Какой язык обучения?',
    stepNativeDescription: 'Мы используем его для переводов, подсказок в тексте и объяснений.',
    stepThemeTitle: 'Как должен выглядеть Lexio?',
    stepThemeDescription: 'Выбери светлую или тёмную тему — потом можно изменить в Настройках.',
    stepLevelTitle: 'Какой у тебя уровень английского?',
    stepLevelDescription: 'Статьи, повторение и практика будут под этот уровень.',
    stepDurationTitle: 'Сколько времени хочешь заниматься в день?',
    stepDurationDescription: 'Ежедневная сессия подстроится под это время.',
    stepDurationLabel: '{minutes} мин',
    stepTopicsTitle: 'О чём любишь читать?',
    stepTopicsDescription: 'Выбери несколько тем — так мы будем рекомендовать статьи. Можно пропустить.',
    stepTopicsSkip: 'Пропустить',
    stepCompleteTitle: 'Готово!',
    stepCompleteDescription: 'Lexio подстроит чтение, словарь и ежедневные сессии под твои ответы.',
    stepCompleteHint: 'Всё это можно изменить в Настройках в любой момент.',
    next: 'Далее',
    back: 'Назад',
    finish: 'Начать',
    languageLabel: 'Язык обучения',
    levelLabel: 'Уровень',
    saveFailed: 'Не удалось сохранить настройки. Попробуй ещё раз.',
  },
  learningDemo: {
    skip: 'Пропустить intro',
    introTitle: 'Один день обучения',
    introPhilosophy:
      'Не нужно заучивать тысячи слов. Читай, сохраняй то, что важно, — и Lexio поможет вспомнить это вовремя.',
    introCta: 'Показать как',
    questStep: 'Шаг {current} из {total}',
    readTitle: 'Читай',
    readBody: 'Прочитай короткий текст. Когда встретишь незнакомое слово — нажми на него.',
    understandTitle: 'Понимай',
    understandBody: 'Мы объясняем слово в контексте. Если оно полезно — сохрани в словарь.',
    vocabularyTitle: 'Твой словарь',
    vocabularyBody: 'Сохранённые слова живут здесь — это твой личный список для изучения.',
    refreshTitle: 'Повторяй',
    refreshBody: 'Позже сегодня или завтра вернись к повторению. Интервальные повторения помогают словам закрепиться.',
    todayTitle: 'Сегодня',
    todayBody: 'Твоя база на день — мы подскажем, что читать и что повторить по прогрессу.',
    tapWordHint: 'Нажми на подсвеченное слово',
    saveWord: 'Сохранить слово',
    saving: 'Сохраняем…',
    saved: 'Сохранено!',
    savedWordLabel: 'Твоё первое слово',
    vocabularyStatusNew: 'Новое',
    continue: 'Далее',
    completeTitle: 'Ты готов',
    completeBody: 'Словарь уже начат. Открой первую статью и тапни любое незнакомое слово.',
    completeCta: 'Начать читать',
    replay: 'Пройти intro снова',
    replayDescription: 'Ещё раз пройти короткое знакомство с приложением.',
    readLabel: 'Читай',
    understandLabel: 'Понимай',
    means: 'Значение',
    inContext: 'В этом предложении',
    definitionLabel: 'Определение',
    exampleLabel: 'Пример',
    vocabularyMoreHint:
      'В словаре также есть прогресс, история повторений и личные заметки — это больше, чем простой список слов.',
    refreshFlipHint: 'Нажми на карточку, чтобы перевернуть',
    refreshCardLabel: 'Карточка повторения',
    refreshMoreHint:
      'В повторении есть разные режимы, статистика сессии и интервальное расписание — это только быстрый взгляд.',
    todayPreviewHint: 'Обычный день выглядит так:',
    todayCardLabel: 'Твой день',
    todayStepReading: 'Продолжить чтение',
    todayStepReadingHint: 'Возьми статью своего уровня',
    todayStepNewWords: 'Сохранять новые слова',
    todayStepNewWordsHint: 'Нажимай на слова при чтении, чтобы пополнять список',
    todayStepRefresh: 'Повторить слова',
    todayStepRefreshHint: 'Повторяй слова к сроку, чтобы они закрепились',
    todayMoreHint: '«Сегодня» собирает чтение, словарь и повторение в одном месте.',
    completeHelpHint:
      'Вопросы? Открой FAQ в сайдбаре (на телефоне — в «Ещё»). Идея или баг — нажми «Обратная связь» прямо над FAQ.',
    translation: 'Перевод',
    playPronunciation: 'Произношение',
    demoArticleTitle: 'Перед школой',
    saveWordFailed: 'Не удалось сохранить слово. Попробуй ещё раз.',
    loadExplanationFailed: 'Не удалось загрузить объяснение слова. Попробуй ещё раз.',
    loadingExplanation: 'Загружаем объяснение…',
  },
  faq: {
    title: 'Частые вопросы',
    intro: 'Коротко о том, как Lexio работает сейчас — чтение, сохранение слов, повторение и аккаунт.',
    feedbackTitle: 'Напиши, что думаешь',
    feedbackHint: 'Лайки, сложности, баги, идеи — мы читаем каждое сообщение.',
    breadcrumb: 'FAQ',
    items: [
      {
        id: 'what-is-lexio',
        question: 'Что такое Lexio?',
        answer:
          'Lexio помогает понимать английский прямо во время чтения. Нажми на слово или фразу — получишь ясное объяснение в контексте, сохрани важное и встретишь это снова через интервальные повторения. Так ты продолжаешь читать, а не открываешь Google.',
      },
      {
        id: 'how-to-start',
        question: 'Как начать?',
        answer:
          'Создай бесплатный аккаунт, подтверди email, выбери родной язык и уровень, пройди короткое демо. Дальше открой Чтение, сохраняй незнакомые слова и делай Повторение, когда карточки готовы.',
      },
      {
        id: 'tap-understand',
        question: 'Как работает «нажал — понял»?',
        answer:
          'В статье нажми на слово (или выдели короткую фразу). Lexio покажет значение для этого предложения, связанные формы, если нужно, и другие значения, если слово многозначное. Перед сохранением выбери нужное значение.',
      },
      {
        id: 'save-meaning',
        question: 'Что происходит, когда я сохраняю слово?',
        answer:
          'Слово попадает в словарь одной карточкой. Если позже добавишь другое значение — оно расширит ту же карточку, без дублей. Повторение идёт по этой карточке — ты практикуешь то, что реально сохранил.',
      },
      {
        id: 'reviews',
        question: 'Как работают повторения?',
        answer:
          'Lexio использует интервальные повторения (SM-2). Сложные слова возвращаются раньше, лёгкие — позже. Открой Повторение с экрана «Сегодня» или из меню, когда есть карточки к сроку — это привычка, которая закрепляет чтение.',
      },
      {
        id: 'email-verify',
        question: 'Зачем подтверждать email?',
        answer:
          'Подтверждение защищает аккаунты от фейковых регистраций. После регистрации по email открой ссылку из письма (проверь «Спам»). Вход через Google уже подтверждён и этот шаг пропускает.',
      },
      {
        id: 'forgot-password',
        question: 'Забыл пароль. Что делать?',
        answer:
          'На странице входа выбери «Забыли пароль?», укажи email и открой ссылку из письма. Ссылка действует час. Если письма нет — проверь «Спам» и тот же email, что у аккаунта.',
      },
      {
        id: 'wrong-translation',
        question: 'Что если перевод кажется неверным?',
        answer:
          'Сначала посмотри «Другие значения» в подсказке — смысл из контекста помечен как «В этом предложении». Если всё равно неверно — напиши через Настройки → Обратная связь или форму на этой странице. Ошибки помогают улучшать Trust Ops.',
      },
      {
        id: 'mobile',
        question: 'Lexio работает на телефоне?',
        answer:
          'Да — это мобильное веб-приложение. Можно добавить на домашний экран. Нижнее меню: Сегодня, Чтение, Словарь и Повторение. Контент прокручивается над меню, действия остаются под рукой.',
      },
      {
        id: 'free',
        question: 'Lexio бесплатный?',
        answer:
          'Да для основного цикла: чтение, понимание по нажатию, словарь и повторения. Часть AI-функций может иметь дневные лимиты. Платные планы могут появиться позже; бесплатный путь останется основой ежедневного понимания английского.',
      },
      {
        id: 'languages',
        question: 'Какие языки поддерживаются?',
        answer:
          'Ты читаешь английский. Учебные переводы — на языке обучения (русский, украинский или польский). Интерфейс тоже может быть на английском, русском, украинском или польском — выбери на лендинге или сохрани язык, выбранный при регистрации.',
      },
    ],
  },
  feedback: {
    like: 'Мне нравится',
    dislike: 'Это сбивает с толку',
    bug: 'Что-то сломалось',
    idea: 'Идея',
    placeholder: 'Что нам стоит знать?',
    emailPlaceholder: 'Твой email',
    submit: 'Отправить',
    done: 'Спасибо — мы получили сообщение.',
    sendAnother: 'Ещё сообщение',
    error: 'Не удалось отправить. Попробуй чуть позже.',
    settingsTitle: 'Обратная связь',
    settingsHint: 'Напиши, что помогает, что путает или что сломалось.',
  },
  settings: {
    appearanceTitle: 'Оформление',
    appearanceDescription: 'Тема и язык интерфейса.',
    uiLanguageLabel: 'Язык интерфейса',
    uiLanguageHint: 'Меню, кнопки и навигация. Отдельно от языка обучения.',
    themeLabel: 'Тема',
    themeLight: 'Светлая',
    themeDark: 'Тёмная',
    learningLanguageLabel: 'Язык обучения',
    learningLanguageHint: 'Для переводов и объяснений на твоём языке.',
  },
}
